import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function PdfViewer({ file, onPageSize, overlay }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const onLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  return (
    <div ref={containerRef} className="w-full">
      <Document
        file={file}
        onLoadSuccess={onLoadSuccess}
        loading={<div className="p-6 text-slate-500">Loading PDF…</div>}
      >
        {isMobile ? (
          <>
            {/* key forces the canvas to unmount/remount on page change, freeing memory */}
            <div key={currentPage} className="relative shadow">
              <Page
                pageNumber={currentPage}
                width={containerWidth || undefined}
                onRenderSuccess={(page) =>
                  onPageSize?.(currentPage, { width: page.width, height: page.height })
                }
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
              {overlay?.(currentPage)}
            </div>
            {numPages > 1 && (
              <div className="mt-3 flex items-center justify-between">
                <button
                  className="btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </button>
                <span className="text-sm text-slate-500">
                  {currentPage} / {numPages}
                </span>
                <button
                  className="btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                  disabled={currentPage === numPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
            <div key={pageNumber} className="relative mb-4 shadow">
              <Page
                pageNumber={pageNumber}
                width={containerWidth || undefined}
                onRenderSuccess={(page) =>
                  onPageSize?.(pageNumber, { width: page.width, height: page.height })
                }
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
              {overlay?.(pageNumber)}
            </div>
          ))
        )}
      </Document>
    </div>
  );
}
